import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreHorizontalIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/components/ui/utils";

type PaginationProps = {
  /** Current page (1-indexed) */
  page: number;
  /** Total number of pages */
  pageCount: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Number of sibling pages shown around the current page. Defaults to 1. */
  siblingCount?: number;
  className?: string;
};

/**
 * Builds the list of page items to render.
 * Returns an array of page numbers and "ellipsis" markers.
 */
function getPageItems(
  page: number,
  pageCount: number,
  siblingCount: number,
): (number | "ellipsis-start" | "ellipsis-end")[] {
  // Always show first, last, current, and siblings around current
  const range = (start: number, end: number) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, i) => start + i);
  };

  const totalSlots = siblingCount * 2 + 5; // siblings + first + last + current + 2 ellipses

  // If total pages fit within the slots, show all pages
  if (pageCount <= totalSlots) {
    return range(1, pageCount);
  }

  const leftSiblingIndex = Math.max(page - siblingCount, 1);
  const rightSiblingIndex = Math.min(page + siblingCount, pageCount);

  const showLeftEllipsis = leftSiblingIndex > 2;
  const showRightEllipsis = rightSiblingIndex < pageCount - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftCount = siblingCount * 2 + 3;
    return [...range(1, leftCount), "ellipsis-end", pageCount];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightCount = siblingCount * 2 + 3;
    return [
      1,
      "ellipsis-start",
      ...range(pageCount - rightCount + 1, pageCount),
    ];
  }

  return [
    1,
    "ellipsis-start",
    ...range(leftSiblingIndex, rightSiblingIndex),
    "ellipsis-end",
    pageCount,
  ];
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  siblingCount = 1,
  className,
}: PaginationProps) {
  if (pageCount <= 1) {
    return null;
  }

  const items = getPageItems(page, pageCount, siblingCount);

  return (
    <nav
      aria-label="Pagination"
      className={cn("flex items-center gap-1", className)}
    >
      <Button
        variant="ghost"
        size="icon-sm"
        isDisabled={page <= 1}
        onPress={() => onPageChange(page - 1)}
        aria-label="Previous page"
      >
        <ChevronLeftIcon />
      </Button>
      {items.map((item) => {
        if (item === "ellipsis-start" || item === "ellipsis-end") {
          return (
            <span
              key={item}
              className="flex h-6 w-6 items-center justify-center"
              aria-hidden
            >
              <MoreHorizontalIcon className="size-3.5 text-muted-foreground" />
            </span>
          );
        }

        const isActive = item === page;
        return (
          <Button
            key={item}
            variant={isActive ? "outline" : "ghost"}
            size="icon-sm"
            onPress={() => onPageChange(item)}
            aria-label={`Page ${item}`}
            aria-current={isActive ? "page" : undefined}
            className={cn(
              "tabular-nums",
              isActive && "pointer-events-none font-semibold",
            )}
          >
            {item}
          </Button>
        );
      })}
      <Button
        variant="ghost"
        size="icon-sm"
        isDisabled={page >= pageCount}
        onPress={() => onPageChange(page + 1)}
        aria-label="Next page"
      >
        <ChevronRightIcon />
      </Button>
    </nav>
  );
}
